import React from 'react';
import { Button } from '@odin-labs/components';
import { Link } from 'react-router-dom';
import {
  GetSiteZonesModeQuery,
  ZonesMode,
  ZonesModeChangeStatus,
  ZonesModeChangeStep,
  ZonesParityDifferenceFragment,
  ZonesParityWorkerFragment,
} from 'apollo/generated/client-operations';
import { actorName, formatAccessDate } from './presentation';

type Change = NonNullable<
  NonNullable<GetSiteZonesModeQuery['getJobsite']>['latestZonesModeChange']
>;
const enableSteps: { value: ZonesModeChangeStep; label: string }[] = [
  { value: ZonesModeChangeStep.Readiness, label: 'Check readiness' },
  { value: ZonesModeChangeStep.DefaultGroup, label: 'Prepare default group' },
  { value: ZonesModeChangeStep.Enrolling, label: 'Enroll workers' },
  { value: ZonesModeChangeStep.Parity, label: 'Compare current access' },
  { value: ZonesModeChangeStep.AdoptAndPush, label: 'Connect and sync groups' },
];
function ParityWorkerName({
  parityWorker: { workerId, worker },
}: {
  parityWorker: ZonesParityWorkerFragment;
}): React.ReactElement {
  // A worker recorded in parity may since have been deleted, or be one this
  // admin may no longer read.
  if (!worker) return <span>Worker not available</span>;
  return (
    <Link className="odin-text-odin-primary" to={`/worker/${workerId}`}>
      {[worker.user.identity?.firstName, worker.user.identity?.lastName]
        .filter(Boolean)
        .join(' ') || 'Unnamed worker'}
    </Link>
  );
}

const statusLabels: Record<ZonesModeChangeStatus, string> = {
  RUNNING: 'In progress',
  SUCCEEDED: 'Succeeded',
  FAILED: 'Failed',
};

function DifferenceList({
  title,
  differences,
}: {
  title: string;
  differences: ZonesParityDifferenceFragment[];
}): React.ReactElement {
  const [visible, setVisible] = React.useState(20);
  if (!differences.length) return null;
  return (
    <div className="odin-space-y-2">
      <h4 className="odin-font-semibold">
        {title} ({differences.length})
      </h4>
      <ul className="odin-space-y-1">
        {differences.slice(0, visible).map(({ identity, check }) => (
          <li
            key={`${check}:${
              'workerId' in identity
                ? identity.workerId
                : identity.externalUserId
            }`}
          >
            {'workerId' in identity ? (
              <ParityWorkerName parityWorker={identity} />
            ) : (
              <span>Avigilon user {identity.externalUserId}</span>
            )}
            {' · '}
            {check === 'ODIN' ? 'Odin membership' : 'Avigilon membership'}
          </li>
        ))}
      </ul>
      {visible < differences.length && (
        <Button
          text="Show more"
          theme="secondary"
          onClick={() => setVisible((count) => count + 20)}
        />
      )}
    </div>
  );
}

export function ZonesModeProgress({
  change,
  timeZone,
}: {
  change: Change;
  timeZone: string;
}): React.ReactElement {
  const [visibleRevocations, setVisibleRevocations] = React.useState(20);
  const [visibleBlockers, setVisibleBlockers] = React.useState(20);
  const enabling = change.fromMode === ZonesMode.Off;
  const steps = enabling
    ? enableSteps
    : [
        { value: ZonesModeChangeStep.Readiness, label: 'Check readiness' },
        {
          value: ZonesModeChangeStep.Parity,
          label: 'Prepare provider transition',
        },
        {
          value: ZonesModeChangeStep.AdoptAndPush,
          label: 'Apply and verify access',
        },
      ];
  const currentStep = steps.findIndex((step) => step.value === change.step);
  const parity = change.parityResult;
  // Only enabling compares against the Off baseline. An active-mode change
  // records a placeholder until its push completes, which is not a blocker.
  const blockers = enabling ? Array.from(new Set(parity?.blockers ?? [])) : [];
  return (
    <div className="odin-rounded-lg odin-bg-gray-50 odin-p-4 odin-space-y-4">
      <div role="status" aria-live="polite" className="odin-space-y-2">
        <h3 className="odin-font-semibold">
          Last change: {statusLabels[change.status]}
        </h3>
        <p className="odin-text-gray-600">
          Started {formatAccessDate(change.startedAt, timeZone)} by{' '}
          {actorName(change.startedBy)}
        </p>
        {change.finishedAt && (
          <p className="odin-text-gray-600">
            Finished {formatAccessDate(change.finishedAt, timeZone)}
          </p>
        )}
        {change.toMode !== ZonesMode.Off && (
          <ol className="odin-space-y-2">
            {steps.map((step, index) => {
              const complete =
                change.status === ZonesModeChangeStatus.Succeeded ||
                index < currentStep;
              const current =
                step.value === change.step &&
                change.status !== ZonesModeChangeStatus.Succeeded;
              return (
                <li
                  key={step.value}
                  aria-current={current ? 'step' : undefined}
                  className={
                    current ? 'odin-font-semibold' : 'odin-text-gray-600'
                  }
                >
                  {complete ? '✓' : `${index + 1}.`} {step.label}
                  {step.value === ZonesModeChangeStep.Enrolling &&
                  change.workersTotal !== null &&
                  change.workersTotal !== undefined
                    ? ` (${change.workersEnrolled ?? 0} of ${
                        change.workersTotal
                      })`
                    : ''}
                  {current && change.status === ZonesModeChangeStatus.Failed
                    ? ' · failed'
                    : ''}
                </li>
              );
            })}
          </ol>
        )}
        {change.toMode === ZonesMode.Off &&
          change.status === ZonesModeChangeStatus.Running && (
            <p>Restoring the site access path…</p>
          )}
      </div>
      {change.message && (
        <p role="alert" className="odin-text-red-700">
          {change.message}
        </p>
      )}
      {parity && (
        <div className="odin-space-y-4">
          {blockers.length > 0 && (
            <div className="odin-space-y-2">
              <h4 className="odin-font-semibold">
                Blockers ({blockers.length})
              </h4>
              <ul className="odin-list-disc odin-pl-5 odin-text-red-700">
                {blockers.slice(0, visibleBlockers).map((message) => (
                  <li key={message}>{message}</li>
                ))}
              </ul>
              {visibleBlockers < blockers.length && (
                <Button
                  text="Show more blockers"
                  theme="secondary"
                  onClick={() => setVisibleBlockers((count) => count + 20)}
                />
              )}
            </div>
          )}
          {/* Only Off → Shadow compares workers; an active-mode change
              verifies the target policy instead and records no differences. */}
          {enabling && (
            <>
              <DifferenceList
                title="Would gain access"
                differences={parity.gained}
              />
              <DifferenceList
                title="Would lose access"
                differences={parity.lost}
              />
            </>
          )}
          {parity.revokedDefaults.length > 0 && (
            <div className="odin-space-y-2">
              <h4 className="odin-font-semibold">
                Would lose access in Enforce ({parity.revokedDefaults.length})
              </h4>
              <p>
                These default memberships were deliberately revoked. They do not
                block Shadow.
              </p>
              <ul>
                {parity.revokedDefaults
                  .slice(0, visibleRevocations)
                  .map((revoked) => (
                    <li key={revoked.workerId}>
                      <ParityWorkerName parityWorker={revoked} />
                    </li>
                  ))}
              </ul>
              {visibleRevocations < parity.revokedDefaults.length && (
                <Button
                  text="Show more revoked defaults"
                  theme="secondary"
                  onClick={() => setVisibleRevocations((count) => count + 20)}
                />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
